import React, { useState, useEffect } from 'react'
import Heading from '../../components/Heading'
import SubHeading from '../../components/SubHeading'
import Select from 'react-select';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { ActionButton } from '../../components/Forms/ActionButton';
import { useGetChallengeList, useGetChallengeCategoriesList } from '../../queries/challenge';
import Chip from '../../components/Chip';
import RichTextEditor from "../../components/Forms/RichTextEditor";
import MediaUpload from '../../components/Forms/MediaUpload';

const LinkChallenge = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const campaignId = state?.campaignId;

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedChallenges, setSelectedChallenges] = useState([]);
    const [filteredChallengeList, setFilteredChallengeList] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [filters, setFilters] = useState({
        category: '',
        complexity: '',
        status: 'active'
    });

    const [coverImage, setCoverImage] = useState(null);
    const [mediaClear, setMediaClear] = useState(false);

    // Get data from API
    const { data: challengeCategoryList, isPending: isChallengeCategoryPending } = useGetChallengeCategoriesList();
    const { data: challengeList, isPending: isChallengeListPending } = useGetChallengeList();

    // Transform category data for react-select
    const categories = challengeCategoryList?.map(category => ({
        value: category._id,
        label: category.name
    })) || [];

    // Transform API data to match expected format
    const transformedChallenges = challengeList?.map(challenge => ({
        _id: challenge._id,
        name: challenge.name,
        category: challenge.category,
        complexity: challenge.complexity,
        description: challenge.description?.replace(/<[^>]*>/g, '') || '', // Strip HTML tags
        points: challenge.points,
        status: challenge.active ? 'active' : 'inactive',
        participantCount: challenge.peopleJoined || 0,
        duration: challenge.recurrent === 'one-time' ? 'One time' : challenge.recurrent || 'N/A'
    })) || [];

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        reset,
    } = useForm({
        defaultValues: {
            sponsorMessage: "",
        }
    });

    // Add this useEffect to filter challenges when category changes
    useEffect(() => {
        if (selectedCategoryId && challengeList) {
            const filtered = challengeList.filter(challenge =>
                challenge.category?._id === selectedCategoryId || challenge.category === selectedCategoryId
            );
            setFilteredChallengeList(filtered);
        } else {
            setFilteredChallengeList(challengeList || []);
        }
    }, [selectedCategoryId, challengeList]);

    // Handle challenge category change
    const handleCategoryChange = (selectedOption) => {
        setSelectedCategory(selectedOption);
        setSelectedCategoryId(selectedOption?.value || "");
    };

    // Filter challenges based on search and filters
    // Use transformed real data for filtering
    const dataToFilter = selectedCategoryId && filteredChallengeList.length > 0
        ? filteredChallengeList.map(challenge => ({
            _id: challenge._id,
            name: challenge.name,
            category: challenge.category,
            complexity: challenge.complexity,
            description: challenge.description?.replace(/<[^>]*>/g, '') || '',
            points: challenge.points,
            status: challenge.active ? 'active' : 'inactive',
            participantCount: challenge.peopleJoined || 0,
            duration: challenge.recurrent === 'one-time' ? 'One time' : challenge.recurrent || 'N/A'
        }))
        : transformedChallenges;

    const filteredChallenges = dataToFilter.filter(challenge => {
        const matchesSearch = challenge.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            challenge.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = !selectedCategory ||
            challenge.category?.name === selectedCategory.label ||
            challenge.category?.name?.toLowerCase().includes(selectedCategory.label.toLowerCase());
        const matchesComplexity = !filters.complexity || challenge.complexity === filters.complexity;
        const matchesStatus = !filters.status || challenge.status === filters.status;

        return matchesSearch && matchesCategory && matchesComplexity && matchesStatus;
    });

    const handleChallengeToggle = (challengeId) => {
        setSelectedChallenges(prev => {
            if (prev.includes(challengeId)) {
                return prev.filter(id => id !== challengeId);
            } else {
                return [...prev, challengeId];
            }
        });
    };

    const getComplexityColor = (complexity) => {
        switch (complexity) {
            case 'easy': return 'bg-green-100 text-green-800';
            case 'moderate': return 'bg-yellow-100 text-yellow-800';
            case 'hard': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const onSubmit = async (data) => {
        try {
            const formData = {
                campaignId,
                linkedChallenges: selectedChallenges,
                sponsorMessage: data.sponsorMessage,
                ...data
            };

            console.log('Linking challenges:', formData);
            // API call to link challenges to campaign
            // await linkChallengesToCampaign(formData);

            // Navigate to next step or back to campaigns
            // navigate('/campaigns', { state: { campaignId, step: 'challenges-linked' } });
        } catch (error) {
            console.error('Error linking challenges:', error);
        }
    };

    // Loading state
    if (isChallengeCategoryPending || isChallengeListPending) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col font-semibold text-gray-900 rounded-none text-base gap-6">
            {/* Header */}
            <div className="flex flex-col px-5 py-4 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                <Heading text="Link Challenges to Campaign" className="mb-2" />
                <p className="text-sm text-gray-600 font-normal">
                    Select challenges that align with your campaign goals and will be available to participants.
                </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
                {/* Sponsor Message Section */}
                <div className="flex flex-col px-5 py-4 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                    <SubHeading text="Sponsor Message" className="mb-4" />
                    <div className="flex flex-col gap-3">
                        <label className="text-[14px] font-[600] text-gray-900">
                            Sponsor Message (to be displayed on the app)
                        </label>
                        <div className="w-full bg-white rounded-md border border-solid border-navBtn">
                            <RichTextEditor
                                placeholder="Enter your sponsor message that will be displayed to app users..."
                                name="sponsorMessage"
                                value={getValues("sponsorMessage")}
                                onChange={(value) => setValue("sponsorMessage", value)}
                                error={errors.sponsorMessage}
                            />
                        </div>
                        {errors.sponsorMessage && <p className="text-red text-sm">{errors.sponsorMessage.message}</p>}
                    </div>
                </div>

                {/* Search and Filters Section */}
                <div className="flex flex-col px-5 py-4 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                    <SubHeading text="Search & Filter Challenges" className="mb-4" />

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        {/* Search Bar */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[14px] font-[600] text-gray-900">Search Challenges</label>
                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Search by name or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary"
                                />
                            </div>
                        </div>

                        {/* Category Filter */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[14px] font-[600] text-gray-900">Category</label>
                            <Select
                                placeholder="All Categories"
                                options={categories}
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                isClearable
                                className="flex-1 text-base"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        backgroundColor: 'transparent',
                                        border: '1px solid rgba(224, 230, 237, 1)',
                                        borderRadius: '0.375rem',
                                        boxShadow: 'none',
                                        padding: '0.25rem',
                                        ":hover": {
                                            borderColor: '#16ad3c',
                                        },
                                    }),
                                    option: (base, { isSelected }) => ({
                                        ...base,
                                        backgroundColor: isSelected ? '#16ad3c' : 'white',
                                        color: isSelected ? 'white' : 'black',
                                        ":hover": {
                                            backgroundColor: isSelected ? '#16ad3c' : 'rgba(52, 199, 89, 0.3)',
                                        },
                                    }),
                                    singleValue: (base) => ({ ...base, color: 'black' }),
                                    placeholder: (base) => ({ ...base, color: 'gray' }),
                                    indicatorSeparator: (base) => ({ ...base, display: 'none' }),
                                }}
                            />
                        </div>

                        {/* Complexity Filter */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[14px] font-[600] text-gray-900">Complexity</label>
                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    value={filters.complexity}
                                    onChange={(e) => setFilters(prev => ({ ...prev, complexity: e.target.value }))}
                                    className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary bg-transparent"
                                >
                                    <option value="">All Levels</option>
                                    <option value="easy">Easy</option>
                                    <option value="moderate">Moderate</option>
                                    <option value="hard">Hard</option>
                                </select>
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[14px] font-[600] text-gray-900">Status</label>
                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    value={filters.status}
                                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                    className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary bg-transparent"
                                >
                                    <option value="">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Selected Challenges Counter */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                        <span className="text-sm text-black">
                            {selectedChallenges.length} challenge(s) selected
                        </span>
                        {selectedChallenges.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setSelectedChallenges([])}
                                className="text-sm text-redDark hover:text-red font-medium"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                </div>

                {/* Challenges List Section */}
                <div className="flex flex-col px-5 py-4 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                    <SubHeading text={`Available Challenges (${filteredChallenges.length})`} className="mb-4" />

                    {(isChallengeCategoryPending || isChallengeListPending) ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        </div>
                    ) : filteredChallenges.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <p>No challenges found matching your criteria.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredChallenges.map((challenge) => (
                                <div
                                    key={challenge._id}
                                    className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${selectedChallenges.includes(challenge._id)
                                        ? 'border-primary'
                                        : 'border-navBtn bg-white hover:border-secondary'
                                        }`}
                                    onClick={() => handleChallengeToggle(challenge._id)}
                                >
                                    {/* Selection Checkbox */}
                                    <div className="absolute top-3 right-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedChallenges.includes(challenge._id)}
                                            onChange={() => handleChallengeToggle(challenge._id)}
                                            className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary"
                                        />
                                    </div>

                                    {/* Challenge Content */}
                                    <div className="pr-8">
                                        <h3 className="font-semibold text-gray-900 text-base mb-2">
                                            {challenge.name}
                                        </h3>

                                        <div className="flex items-center gap-2 mb-2">
                                            <Chip
                                                value={challenge.category?.name || 'Unknown'}
                                                className="text-xs px-2 py-1 bg-blue-100 text-blue-800"
                                            />
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getComplexityColor(challenge.complexity)}`}>
                                                {challenge.complexity?.charAt(0).toUpperCase() + challenge.complexity?.slice(1) || 'Unknown'}
                                            </span>
                                        </div>

                                        <p className="text-sm text-gray-600 mb-3 line-clamp-1">
                                            {challenge.description}
                                        </p>

                                        <div className="flex items-center justify-between text-sm text-gray-500">
                                            <div className="flex items-center gap-4">
                                                <span className="font-medium text-green-600">
                                                    {challenge.points || 0} pts
                                                </span>
                                                <span>
                                                    👥 {challenge.participantCount || 0}
                                                </span>
                                            </div>
                                            <span className="text-xs">
                                                {challenge.duration || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex flex-col px-5 py-4 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                    <Heading text="Reward Info" className="mb-2" />
                    <p className="text-sm text-gray-600 font-normal">
                        Set up rewards and incentives for campaign participants. Configure point multipliers, special badges, or prize pools to motivate engagement and completion of linked challenges.
                    </p>
                </div>

                <div className="flex flex-col gap-3 px-5 py-4 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">
                    <SubHeading text="Reward Details (Campaign Specific)" className="mb-4" />
                    <div className='flex items-center gap-3'>
                        <div className="flex flex-col gap-2 basis-1/2">
                            <label className="text-[14px] font-[600] text-gray-900">Type of Reward</label>
                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Select type of reward"
                                    className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary"
                                    {...register("rewardType", { required: "Reward type is required" })}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 basis-1/2">
                            <label className="text-[14px] font-[600] text-gray-900">Max Coupon Count</label>
                            <div className="flex gap-2.5 items-center w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    placeholder="Select Max Coupon Count"
                                    className="flex-1 px-4 py-2.5 rounded-md text-base outline-primary"
                                    {...register("maxCouponCount", { required: "Max Coupon Count is required" })}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex flex-col gap-0'>
                        <label className='text-[14px] font-[600] text-gray-900'>Discount Mode</label>
                        <div className="flex gap-12 items-center mt-1 w-full bg-white border border-solid border-navBtn px-4 py-3 rounded-md text-slate-400">
                            {['Digital Coupon', 'Others'].map((platform) => (
                                <div key={platform} className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="discountMode"
                                        id={`promotion-frequency-${platform}`}
                                        value={platform}
                                        {...register("discountMode")}
                                        className="w-5 h-5 text-white border-gray-300  focus:ring-black accent-black"
                                    />
                                    <label
                                        htmlFor={`promotion-frequency-${platform}`}
                                        className="text-[12px] font-[600] text-gray-700 capitalize"
                                    >
                                        {platform}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='flex flex-col gap-2'>
                        <MediaUpload
                            className={`h-auto`}
                            clearMedia={mediaClear}
                            label={"Upload Coupon Media"}
                            existingImage={coverImage}
                            setImageUpload={setCoverImage}
                            stateImage={coverImage}
                            setStateImageUpload={setCoverImage}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                    <ActionButton
                        variant="cancel"
                        text="Back"
                        handleSubmit={() => navigate('/campaign-management/new-campaign', { state: { campaignId } })}
                        type="button"
                    />

                    <div className="flex items-center gap-3">
                        <ActionButton
                            variant="reset"
                            text="Skip"
                            handleSubmit={() => {
                                reset();
                                setSelectedChallenges([]);
                                setSelectedCategory(null);
                                setSelectedCategoryId("");
                                setSearchTerm("");
                                navigate('/campaigns', { state: { campaignId, step: 'challenges-skipped' } });
                            }}
                            type="button"
                        />
                        <ActionButton
                            variant="submit"
                            text={`Link ${selectedChallenges.length} Challenge${selectedChallenges.length !== 1 ? 's' : ''}`}
                            disabled={selectedChallenges.length === 0}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default LinkChallenge